import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange, Matcher } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface DatePickerRangeProps {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: Matcher | Matcher[];
  className?: string;
  /**
   * Shown under the calendar while it is open. The picker is modal: the page
   * behind it is covered and hidden from assistive technology until it closes,
   * so a sentence about the pick belongs here to be seen and announced.
   */
  footer?: React.ReactNode;
  /** Ties the trigger to a sentence about the picked range, read when focus returns to it. */
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export function DatePickerRange({
  value,
  onChange,
  placeholder = "Pick a date range",
  disabled,
  className,
  footer,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
}: DatePickerRangeProps) {
  const label = value?.from
    ? value.to
      ? `${format(value.from, "LLL dd, y")} – ${format(value.to, "LLL dd, y")}`
      : format(value.from, "LLL dd, y")
    : placeholder;

  return (
    <Popover modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", !value?.from && "text-muted-foreground", className)}
          aria-describedby={describedBy}
          aria-invalid={invalid}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onChange}
          disabled={disabled}
          numberOfMonths={2}
          initialFocus
        />
        {footer}
      </PopoverContent>
    </Popover>
  );
}
